import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  ChartSection,
  Card,
  InfoBox,
  FigureCaption,
  SourcesSection,
  ImageContainer,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

// @ts-ignore
import angebot1 from "../../assets/angebot1.png";

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
  }
  p {
    margin-bottom: 8px;
  }
`;

export const WasserangebotRoute = React.memo(function WasserangebotRoute() {
  const navigate = useNavigate();
  const { t } = useTexts();

  const tabs = [
    {
      title: t("b.tab.1.title", "Die Bedeutung von Wasser"),
      path: "/wasserklaerer/wasserangebot/angebot",
      isActive: true,
    },
    {
      title: t("b.tab.2.title", "Wo kommt das Wasser her?"),
      path: "/wasserklaerer/wasserangebot/herkunft",
      isActive: false,
    },
    {
      title: t("b.tab.3.title", "Wasserverbrauch in Deutschland"),
      path: "/wasserklaerer/wasserangebot/verbrauch",
      isActive: false,
    },
    {
      title: t("b.tab.4.title", "Entwicklung des Wasserverbrauchs"),
      path: "/wasserklaerer/wasserangebot/entwicklung",
      isActive: false,
    },
  ];

  const chartOptions = {
    chart: {
      type: "pie",
      height: 500,
    },
    title: {
      text: t("b1.chart.title", "Wasserdargebot und Wassernutzung 2022"),
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        color: "#2c3e50",
      },
    },
    subtitle: {
      text: t(
        "b1.chart.subtitle",
        "Potentielles Wasserdargebot: 176 Mrd. Kubikmeter = 100%"
      ),
      style: {
        fontSize: "16px",
        color: "#666",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
        },
        showInLegend: true,
        innerSize: "40%",
        colors: ["#1565c0", "#4fc3f7", "#00897b", "#26a69a", "#66bb6a"],
      },
    },
    series: [
      {
        name: "Wassernutzung",
        data: [
          {
            name: "Verbleibende Wassermenge für Ökosystem",
            y: 89.9,
            color: "#4fc3f7",
          },
          { name: "Energieversorgung", y: 3.9, color: "#00897b" },
          { name: "Öffentliche Wasserversorgung", y: 3.0, color: "#1e88e5" },
          {
            name: "Bergbau und Verarbeitendes Gewerbe",
            y: 3.0,
            color: "#26a69a",
          },
          { name: "Landwirtschaft", y: 0.3, color: "#66bb6a" },
        ],
      },
    ],
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    credits: { enabled: false },
  };

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("b1.title", "Die Bedeutung von Wasser")}</h1>

        <TabContainer>
          {tabs.map((tab, index) => (
            <TabButton
              key={index}
              isActive={tab.isActive}
              onClick={() => !tab.isActive && navigate(tab.path)}
            >
              {tab.title}
            </TabButton>
          ))}
        </TabContainer>

        <Card>
          <h2>{t("b1.intro.title", "Kein Wasser - kein wir")}</h2>
          <p>
            {t(
              "b1.intro.text",
              "Wasser sichert unser Überleben und ist in jedem Aspekt unseres Alltags präsent. Es ist unser wichtigstes Gut. Daher müssen wir als Gesellschaft ein Bewusstsein dafür schaffen, dieses Gut auch zu schützen. Weltweit treten vermehrt Wasserknappheitskrisen auf, doch betreffen diese Probleme auch uns? Hat Wasserknappheit mittlerweile auch Deutschland erreicht?"
            )}
          </p>
        </Card>

        <ChartSection>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          <FigureCaption>
            {t(
              "b1.chart.caption",
              "Abbildung 1: Wasserdargebot und Wassernutzung 2022 (eigene Darstellung nach Umweltbundesamt [1])"
            )}
          </FigureCaption>
        </ChartSection>

        {/* Sektion 1: Wasserdargebot */}
        <Card>
          <h2>
            {t(
              "b1.section1.title",
              "Was wird unter dem potenziellen Wasserdargebot eines Landes verstanden?"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section1.text",
                "Das potenzielle Wasserdargebot eines Landes beschreibt, wie wasserreich ein Land oder eine Region ist. Es umfasst die Menge an Grund- und Oberflächensüßwasser, die wir theoretisch nutzen könnten. [2]"
              )
            )}
          </p>
        </Card>

        {/* Sektion 2: Ermittlung */}
        <Card>
          <h2>
            {t(
              "b1.section2.title",
              "Wie wird das potenzielle Wasserdargebot ermittelt?"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section2.text",
                "Für eine sogenannte Wasserbilanz werden über mehrjährige Zeiträume verschiedene hydrologische Parameter erfasst: Unter anderem Niederschlag, Verdunstung, oberirdische und unterirdische Zuflüsse ins und Abflüsse aus dem Land und die Grundwasserneubildung. Betrachtet werden zum Beispiel henden Wassermengen aus Seen oder Grundwasserreserven, da sich das potenzielle Wasserdargebot ausschließlich auf die erneuerbaren Wasserressourcen eines Gebiets bezieht. Der Grundwasserstand aus Seen und tief liegenden Grundwasserreserven gelten hingegen als Speicher und nicht als Zuflüsse. [2] [3]"
              )
            )}
          </p>
        </Card>

        {/* Sektion 3: Nutzungsgrad */}
        <Card>
          <h2>
            {t(
              "b1.section3.title",
              "Wie hoch ist Deutschlands Wasserdargebot? – Der Nutzungsgrad"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section3.text",
                "Deutschland verfügte im Jahr 2022 über ein Wasserdargebot von 176 Milliarden m³. [1] Das Wasserdargebot umfasst hierbei die Menge an Grund- und Oberflächenwasser, die wir theoretisch nutzen können. [4] Ein ähnlich hohes Wasserdargebot weist beispielsweise Schweden auf, mit etwa 179 Milliarden m³ (2014). Deutlich niedriger liegt dieser Wert hingegen in Teilen Südosteuropas – so verzeichnete Rumänien lediglich 42,3 Milliarden m³ an Wasserressourcen (2014). [2]"
              )
            )}
          </p>

          <h3>
            {t(
              "b1.section3.nutzung.title",
              "Deutschlands Wassernutzung im Überblick"
            )}
          </h3>
          <p>
            {processInlineFormatting(
              t(
                "b1.section3.nutzung.text1",
                "Diese Zahlen allein erlauben jedoch noch keine Aussage über den Grad der Wassernutzung in einem Land. Ein zentraler Indikator hierfür ist der Anteil des genutzten Wassers am gesamten Wasserdargebot. In Deutschland wurden im Jahr 2022 insgesamt 18 Milliarden Kubikmeter Wasser genutzt. [1] Das entspricht nur etwa 10 % des verfügbaren Dargebots. Die restlichen 90 % verbleiben im natürlichen Wasserkreislauf und tragen weiterhin zur ökologischen Stabilität bei. [2]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section3.nutzung.text2",
                "Wichtig hierbei zu verstehen ist, dass die Zahl von 10 % sogar als großzügig eingeschätzt werden kann, denn ein großer Teil der genutzten Wassermenge wird für Kühlzwecke in der Energieversorgung verwendet. Der überwiegende Teil dieses Kühlwassers gelangt dabei nach der Nutzung direkt wieder in die Oberflächengewässer und steht somit erneut zur Verfügung. [2]"
              )
            )}
          </p>
        </Card>

        {/* Sektion 4: Wasserknappheit */}
        <Card>
          <h2>
            {t("b1.section4.title", "Herrscht in Deutschland Wasserknappheit?")}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text1",
                "Im internationalen Vergleich gilt ein Nutzungsgrad von über 20 % als Schwelle, ab der von „Wasserstress“ gesprochen wird. Deutschland liegt unterhalb dieses kritischen Werts und zählt demnach in der Gesamtbetrachtung nicht zu den wassergefährdeten Ländern. [2] [4] Deutschland hat die Schwelle zum Wasserstress letztmalig 2004 überschritten, die gesamten Wasserentnahmen lagen damals laut Statistischem Bundesamt bei 20,2 Prozent. [4]"
              )
            )}
          </p>
          <p>
            {t(
              "b1.section4.text2",
              "Aber: Auch wenn das Gesamtbild noch gut aussieht, überdecken Gesamtbilanzen regionale Hotspots. Es gibt einige Indikatoren, die uns eine Warnung geben, dass auch wir in Deutschland anfangen sollten, sorgsam mit unserer wichtigsten Ressource umzugehen."
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text3",
                "Aktuelle Trockenheit in Deutschland: Der Juni 2025 stellte bereits den fünften Monat in Folge dar, der in Deutschland deutlich trockener ausfiel als das klimatologische Mittel. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text4",
                "Zwar ließe sich diese Abweichung grundsätzlich als zufällige, natürliche Schwankung innerhalb des Klimasystems interpretieren, doch deutet der aktuelle Stand der Forschung darauf hin, dass Phasen ausgeprägter Trockenheit künftig häufiger und länger andauern werden. Damit zeichnen sich Dürren zunehmend als Teil einer sich etablierenden „neuen Normalität“ ab. [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text5",
                "Aufeinander folgende trockene Sommer haben negative Auswirkungen auf die Landwirtschaft, Grundwasserneubildung und Ökosysteme. Wassermangel in der Landwirtschaft wirkt doppelt belastend, da dadurch kompensierend zusätzliches Grundwasser entnommen werden muss für die Bewässerung. Ohne Wasserversorgungskonzepte und Leitlinien wird zukünftig das Risiko eines Ungleichgewichts zwischen Wasserbedarf und Wasserdargebot steigen. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text6",
                "Generell waren die Jahre 2018, 2019, 2020 und 2022 deutlich trockener als sonst, was für regionale Wasserengpässe in Deutschland gesorgt hat. Monate mit niedrigen Grundwasserständen mit teils Rekordtiefständen traten signifikant häufiger auf. [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text7",
                "In der letzten Dekade 2011-2020 war der Rückgang unseres Wasserdargebots in einigen Regionen zudem statistisch signifikant. Auswertungen haben gezeigt, dass die Grundwasserneubildung in der letzten Dekade zurückgegangen ist, vor allem in den Regionen Niedersachsen, Teile NRWs, Baden-Württemberg und Bayern. [4] [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text8",
                "Auch Wassernutzungskonflikte treten immer häufiger auf in Deutschland. Bis zum Stichtag 31.12.2022 wurden in Deutschland insgesamt 358 Wassernutzungskonflikte (WNK) erfasst. Die Berichte reichen von den 1950er-Jahren bis 2022. Zu berücksichtigen ist, dass vor 2015 pro Jahr höchstens zwei Wassernutzungskonflikte dokumentiert wurden. Die Konflikte reichen von Qualität und Quantität von Grundwasser, Oberflächenwasser und Trinkwasser bis hin zur Bepreisung, Infrastrukturproblemen und Verteilungskonflikten. [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text9",
                "Noch hat sich das alles aber nicht auf unser Trinkwasser aus dem Hahn ausgewirkt. Die Trinkwasserversorgung verzeichnete bislang in Deutschland keine Ausfälle. In besonders betroffenen Regionen kam im Sommer 2018 die private Eigenversorgung jedoch teilweise zum Erliegen, weil Hausbrunnen trockenfielen. Jedoch kam es im Sommer 2018 laut Angaben der Wasserversorgungsunternehmen – abgesehen von wenigen lokalen Ausnahmen – zu keinen Ausfällen in der zentralen Trinkwasserversorgung. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text10",
                "Die Ursachen für Wasserknappheit in verschiedenen Regionen Deutschlands sind vielfältig und umfassen natürliche Klimavariabilität, anthropogene Klimaveränderungen, Einflüsse durch Bergbau, hohe Bedarfe in Metropolregionen und zunehmende Wasserentnahmen für die Landwirtschaft, die zusätzlich Wasserqualitätsprobleme durch hohe Nitratbelastung mit sich bringen. [5] Punktuelle Überlastungen von Verteilnetzen entstehen durch übermäßige Wasserentnahme zu warmen Tageszeiten, was Versorger bis an Kapazitätsgrenzen trieb in den trockenen Jahren. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section4.text11",
                "Wasserknappheit ist eine sehr komplexe Thematik, da viele Faktoren in eine Bewertung einfließen: klimatische Änderungen und deren Auswirkungen auf Niederschläge, Evapotranspiration, Topografie, Bodenart, Bodenfeuchte und Szenarien der zukünftigen Wasserentnahmen durch Bevölkerungsentwicklungen. Aufgrund teils fehlender Datenlage ist eine genaue Bewertung und Prognose von Wasserknappheit schwer. Dennoch unternahm das Umweltbundesamt den Versuch, eine möglichst präzise Karte zu erstellen, die deutschlandweit künftige Hotspots aufdecken kann. Dafür wurden verfügbare Daten gesammelt, und einzelne Regionen nach verschiedenen Kriterien mittels eines Punktesystems bewertet. Die Aufsummierung der Punkte und grafische Darstellung der Regionen erlaubt uns einen Einblick, welchen Regionen in Deutschland Herausforderungen bevorstehen könnten: [5]"
              )
            )}
          </p>

          <ImageContainer>
            <img
              src={angebot1}
              alt="Grundwasserbilanzrisikogebiete nach Punktesystem der Studie WADKlim"
            />
            <FigureCaption>
              {t(
                "b1.image2.caption",
                "Abbildung 2: Grundwasserbilanzrisikogebiete (GWBRG) nach Punktesystem der Studie WADKlim [5] in eigener Darstellung"
              )}
            </FigureCaption>
          </ImageContainer>
        </Card>

        {/* Sektion 5: Zwischenfazit */}
        <Card>
          <h2>{t("b1.section5.title", "Zwischenfazit")}</h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section5.text",
                "Deutschland weist in der Gesamtbetrachtung keinen Wasserstress auf. Allerdings nehmen regional die Tendenzen für generelle Trockenheit und sinkende Grundwasserspiegel zu. Durch den Klimawandel wird Trockenheit in Zukunft immer wahrscheinlicher. Diese Engpässe können neue Nutzungskonflikte auslösen oder bestehende Konflikte verschärfen. Daher ist eine nachhaltige Wassernutzung erforderlich, um eine sichere und gerechte Versorgung aller Nutzergruppen zu gewährleisten. [4] [5]"
              )
            )}
          </p>
        </Card>

        {/* Sektion 6: Maßnahmen */}
        <Card>
          <h2>
            {t(
              "b1.section6.title",
              "Was wird bereits gemacht, um Deutschland besser auf Wasserknappheit vorzubereiten?"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section6.text1",
                "Bereits heute werden regionsspezifische Maßnahmen gegen Wasserknappheit umgesetzt, etwa Fernwasserleitungen und zeitweilige Begrenzungen von Wasserentnahmen. Regional werden übermäßige Entnahmen bereits durch gezielte Grundwasseranreicherung durch die Versickerung von Wasser kompensiert. (Kapitel A2: Wo kommt das Wasser her?) [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section6.text2",
                "Am 15.03.2023 hat das Bundeskabinett die Nationale Wasserstrategie beschlossen. Darin gibt es das „Aktionsprogramm Wasser“, welches der Operationalisierung der Nationalen Wasserstrategie dient. Das Programm enthält 78 Maßnahmen zur Anpassung der Wasserwirtschaft an den Klimawandel, zur Minderung von Stoffeinträgen und zur Stärkung der Bewusstseinsbildung. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section6.text3",
                "Geplant sind beispielsweise der Ausbau von Daten- und Prognosesystemen zum Wasserhaushalt sowie ein verbessertes Grundwassermonitoring. Dies ermöglicht frühzeitige Reaktionen auf langfristige Veränderungen und die kurzfristige Steuerung von Entnahmen, um Übernutzung zu vermeiden. Zentral sind außerdem Renaturierung und Maßnahmen zum Wasserrückhalt in der Fläche. [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section6.text4",
                "Das Umweltbundesamt ergänzt dies mit der Studie WADKlim. Sie stellt Maßnahmenkataloge zur Minderung von Wassernutzungskonflikten vor, verweist auf bestehende Wassernutzungskonzepte der Länder und bewertet spezifische Ansätze zur Erhöhung des Wasserrückhalts. [5]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section6.text5",
                "Politische Entscheidungsträger*innen und weitere Stakeholder sollten die beschriebenen Strategien in ihre Entscheidungen integrieren. Daraus ergeben sich konkrete Empfehlungen für Politik, technologische Innovationen und gesellschaftliche Praxis. Hervorzuheben ist die Wasserwiederverwendung in urbanen Räumen, etwa zur Bewässerung von Grünflächen, insbesondere in Wasserbilanzrisikogebieten. Nur koordiniertes Handeln sichert die Wasserverfügbarkeit in Deutschland langfristig. [5]"
              )
            )}
          </p>
        </Card>

        {/* Sektion 7: Übermäßige Grundwasserentnahme */}
        <Card>
          <h2>
            {t(
              "b1.section7.title",
              "Was würde bei übermäßiger Grundwasserentnahme passieren?"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section7.text1",
                "Eine übermäßige Grundwasserentnahme führt immer zu einer Absenkung des Grundwasserspiegels. Dadurch können grundwasserabhängige Ökosysteme - beispielsweise feuchte Lebensräume - geschädigt werden. Ein anderes Beispiel zeigt sich an einigen Grundwasserkörpern entlang der Ostseeküste - einer Region mit hoher touristischer Nutzung. Auch hier werden sinkende Grundwasserstände verzeichnet, weshalb eine Mobilisierung von salzhaltigem Wasser oder Brackwasser die Folge ist. Die Konsequenz ist eine schleichende Versalzung des Grundwassers. [2]"
              )
            )}
          </p>
          <p>
            {t(
              "b1.section7.text2.main",
              "Ein extremes Beispiel für Wasserknappheit zeigt sich in der ökologischen Katastrophe des Aralsees, in welchem übermäßiger Nutzung zu einer unbewohnbaren Salzwüste geführt hat und ein zerstörtes Ökosystem zurückgelassen hat."
            )}{" "}
            <a
              href="/wasserklaerer/wasserknappheit/fussabdruck"
              onClick={(e) => {
                e.preventDefault();
                navigate("/wasserklaerer/wasserknappheit/fussabdruck");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              style={{ color: "var(--primaryColor)" }}
            >
              {t("b1.section7.text2.link", "(Kapitel F3: Wasser-Fußabdruck)")}
            </a>
          </p>
        </Card>

        {/* Sektion 8: Wasser sparen */}
        <Card>
          <h2>
            {t(
              "b1.section8.title",
              "Müssen wir als Bürger jetzt Wasser sparen oder nicht? Wie verhalte ich mich richtig?"
            )}
          </h2>
          <p>
            {processInlineFormatting(
              t(
                "b1.section8.text1",
                "Das Umweltbundesamt appelliert: „Alle Wassernutzer*innen sind aufgefordert, die Wasserressourcen zu schonen, d.h. mit Wasser sparsam umzugehen und das entnommene Wasser so effizient wie möglich zu verwenden sowie die Gewässer und das Grundwasser nicht zu verschmutzen.“ [4]"
              )
            )}
          </p>
          <p>
            {processInlineFormatting(
              t(
                "b1.section8.text2",
                "Diese Aussage ist aus mehreren Gründen sinnvoll. Obwohl Deutschland insgesamt keinen Wasserstress aufweist, zeigt die obige Karte, dass Wasserbilanzrisikogebiete bereits Gegenwart sind. Durch den Klimawandel nehmen regionale Trockenphasen und Wasserengpässe zu, die Grundwasserneubildung ist in mehreren Regionen rückläufig und dokumentierte Nutzungskonflikte häufen sich. Jede vermiedene Entnahme senkt lokal das Engpassrisiko, stabilisiert Grundwasserstände und reduziert Konflikte zwischen Haushalten, Landwirtschaft, Industrie und Ökosystemen. Je mehr wir unsere Ressourcen jetzt schützen, desto weniger werden zukünftige Generationen belastet. Zudem gilt die Faustregel: Je geringer der menschliche Eingriff in den natürlichen Wasserhaushalt, desto besser geht es unserem Ökosystem. [2]"
              )
            )}
          </p>
        </Card>

        {/* Sektion 9: Verstopfte Leitungen */}
        <Card>
          <h2>
            {processInlineFormatting(
              t("b1.section9.title", "Kehrseite: „Verstopfte Leitungen“ [6]")
            )}
          </h2>
          <p>
            {t(
              "b1.section9.text1",
              "Trink- und Abwassernetze in Deutschland sind für höhere Durchflüsse ausgelegt, als heute üblich ist. Der Ausbau erfolgte in Zeiten, in denen mit stetig steigendem Verbrauch gerechnet wurde. Tatsächlich sind private wie gewerblich-industrielle Verbräuche seit den 1970er-Jahren deutlich gesunken. In einigen Regionen fließt das Wasser daher zu langsam. Mögliche Folgen sind längere Verweilzeiten, Keimbildung, unvollständiger Austrag von Ablagerungen, Bildung von Schwefelsäure und Korrosion; bei Stagnation kann Rost entstehen."
            )}
          </p>
          <p>
            {t(
              "b1.section9.text2",
              "Technische Gegenmaßnahme sind Netzspülungen. Ein Rückbau wäre theoretisch möglich, ist jedoch kostenintensiv. Ökologisch sinnvoll ist für Spülzwecke der Einsatz von Betriebswasser, z. B. gereinigtem Ablauf aus Kläranlagen, statt Trinkwasser."
            )}
          </p>
          <p>
            {t(
              "b1.section9.text3",
              "„Also ist es egal ob wir Wasser sparen oder nicht. Weil doch sowieso durchgespült wird.“ Diese Aussage trifft nur teilweise zu, denn Netzspülungen sind nur mancherorts notwendig. Die Aussage trifft punktuell zu. Das ist kein generelles Argument gegen sparsamen Umgang mit Wasser. Für weitere und genauere Informationen kann dir dein zuständiger Wasserversorger vor Ort genauere Auskunft geben."
            )}
          </p>
        </Card>

        <HighlightBox>
          <p>
            {processInlineFormatting(
              t(
                "b1.section10.text",
                "Es geht nicht darum, dass du deinen Wasserhahn jetzt verkrampft weniger nutzen sollst. Es geht um einen bewussten und sparsamen Umgang mit Wasser, welcher nicht nur die Umwelt entlastet, sondern auch den Bedarf an aufwendigen und kostenintensiven Ausgleichsmaßnahmen reduziert. Vielleicht solltest du im Sommer an einem heißen Mittag nicht unbedingt deinen Pool auffüllen oder deinen Garten gießen. Besser am kühlen Morgen oder Abend. Zur Bewässerung kann statt Leitungswasser auch gesammeltes Regenwasser verwendet werden. [4] Insbesondere mit Warmwasser sollte sorgsam umgegangen werden; [6] Wasch- und Geschirrspülmaschinen nur bei voller Beladung betreiben; das Vollbad durch eine Dusche ersetzen. Maßnahmen zur Verringerung der Gewässerverschmutzung erhöhen die tatsächliche Wasserverfügbarkeit. Dazu zählen der bevorzugte Kauf von Lebensmitteln aus ökologischer Landwirtschaft, der Verzicht auf Pflanzenschutzmittel und Biozide in Garten und Haushalt sowie die ordnungsgemäße Entsorgung von Arzneimitteln. [4] Weitere Hinweise und Tipps findest du im Kapitel „Wassernutzung im Haushalt“. Der nachhaltige Schutz unserer Wasserressourcen ist nicht allein eine Aufgabe für Behörden und Fachleute, sondern eine gesamtgesellschaftliche Verantwortung!"
              )
            )}
          </p>
        </HighlightBox>

        {/* Literaturverzeichnis */}
        <SourcesSection>
          <h3>{t("b1.literatur.title", "Literaturverzeichnis")}</h3>
          <ol>
            <li id="quelle-1">
              <LinkifyText>
                {t(
                  "b1.literatur.ref1",
                  `[1] Umweltbundesamt (Hrsg.), „Wasserdargebot und Wassernutzung 2022", Umweltbundesamt. Zugegriffen: 29. Oktober 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/bild/wasserdargebot-wassernutzung-2022`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-2">
              <LinkifyText>
                {t(
                  "b1.literatur.ref2",
                  `[2] C. Baumgarten, J. Rechenberg, S. Richter, I. Chorus, L. Vigelahn, und O. Schmoll, „Wassersparen in Privathaushalten: sinnvoll, ausgereizt, übertrieben? - Fakten, Hintergründe, Empfehlungen", Umweltbundesamt, 2014. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/hgp_wassersparen_in_privathaushalten_web.pdf`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-3">
              <LinkifyText>
                {t(
                  "b1.literatur.ref3",
                  `[3] Dr. G. Böhme-Korn, C. Friederich, M. Kämper, P. Krahe, L. Nordmeyer, „Textbaustein zur Analyse und Nutzung des Wasserdargebotes für die 2. Bewirtschaftungspläne WRRL", LAWA - Bund/Länderarbeitsgemeinschaft Wasser, 2014. [Online]. Verfügbar unter: https://www.wasserblick.net/servlet/is/142653/WRRL_2.7.13_TB_Wasserdargebot_2._BP.pdf?command=downloadContent&filename=WRRL_2.7.13_TB_Wasserdargebot_2._BP.pdf`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-4">
              <LinkifyText>
                {t(
                  "b1.literatur.ref4",
                  `[4] Umweltbundesamt (Hrsg.), „Trockenheit in Deutschland – Fragen und Antworten", Umweltbundesamt, 2025. Zugegriffen: 13. Oktober 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/themen/wasser/extremereignisseklimawandel/trockenheit-in-deutschland-fragen-antworten`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-5">
              <LinkifyText>
                {t(
                  "b1.literatur.ref5",
                  `[5] U. Stein, J. Tröltzsch, R. Vidaurre, H. Schritt, B. Bueb, J. Reineke, et al., „Auswirkung des Klimawandels auf die Wasserverfügbarkeit - Anpassung an Trockenheit und Dürre in Deutschland - Ergebnisse der Grundlagenstudie ‚WADKlim' im Auftrag des Umweltbundesamts", Dessau-Roßlau: Umweltbundesamt, 2024. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/2875/dokumente/zusammenfassung_und_ergebnisse.pdf`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
            <li id="quelle-6">
              <LinkifyText>
                {t(
                  "b1.literatur.ref6",
                  `[6] J. Vatter, „Dürre in Deutschland: Wasserspar-Mythen im Check", WWF Blog. Zugegriffen: 20. Oktober 2025. [Online]. Verfügbar unter: https://blog.wwf.de/duerre-wassersparen-mythen/`
                ).replace(/^\[\d+\]\s*/, "")}
              </LinkifyText>
            </li>
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
